declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
    fbq?: ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void;
      queue?: unknown[];
      loaded?: boolean;
      version?: string;
      push?: (...args: unknown[]) => void;
      getState?: () => { pixels?: Array<{ id?: string }> };
    };
    _fbq?: Window['fbq'];
    ttq?: {
      load: (pixelId: string, opts?: Record<string, unknown>) => void;
      page: (...args: unknown[]) => void;
      track: (event: string, props?: Record<string, unknown>, opts?: Record<string, unknown>) => void;
      identify: (props?: Record<string, unknown>) => void;
      instances?: (...args: unknown[]) => unknown;
      push?: (...args: unknown[]) => void;
      _i?: Record<string, unknown>;
      _t?: Record<string, number>;
      _o?: Record<string, unknown>;
      methods?: string[];
      setAndDefer?: (target: unknown, method: string) => void;
      instance?: (pixelId: string) => unknown;
      [key: string]: unknown;
    };
    TiktokAnalyticsObject?: string;
    __joyH5FbPixelInited?: Record<string, true>;
    __joyH5GoogleInited?: Record<string, true>;
    __joyH5TtPixelInited?: Record<string, true>;
  }
}

export type H5TrackingConfig = {
  facebookPixelId?: string | null;
  /** GTM-XXXX (Tag Manager) hoặc G-XXXX (GA4 Measurement ID trực tiếp) */
  gtmContainerId?: string | null;
  /** TikTok Pixel code (Admin → Product) */
  tiktokPixelCode?: string | null;
};

const LOG = '[H5 tracking]';

function normId(v?: string | null): string {
  return v != null ? String(v).trim() : '';
}

function isGtmId(id: string) {
  return /^GTM-[A-Z0-9]+$/i.test(id);
}

function isGa4Id(id: string) {
  return /^G-[A-Z0-9]+$/i.test(id);
}

/** Pixel đã init (của H5, GTM, hoặc queue chưa load xong). */
function isFbPixelReady(pixelId: string): boolean {
  if (window.__joyH5FbPixelInited?.[pixelId]) return true;
  try {
    const pixels = window.fbq?.getState?.()?.pixels;
    if (Array.isArray(pixels) && pixels.some((p) => String(p?.id) === pixelId)) {
      return true;
    }
  } catch {
    // ignore
  }
  const queue = window.fbq?.queue;
  if (Array.isArray(queue)) {
    for (const item of queue) {
      if (Array.isArray(item) && item[0] === 'init' && String(item[1]) === pixelId) {
        return true;
      }
    }
  }
  return false;
}

function ensureGtag() {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag === 'function') return;
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  };
}

function ensureFbqStub() {
  if (window.fbq) return;
  const n: NonNullable<Window['fbq']> = function (...args: unknown[]) {
    if (n.callMethod) {
      n.callMethod(...args);
    } else {
      (n.queue = n.queue || []).push(args);
    }
  } as NonNullable<Window['fbq']>;
  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  window.fbq = n;

  if (!document.querySelector('script[src*="connect.facebook.net"][src*="fbevents.js"]')) {
    const t = document.createElement('script');
    t.async = true;
    t.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const s = document.getElementsByTagName('script')[0];
    s?.parentNode?.insertBefore(t, s);
  }
}

/**
 * TikTok Pixel base code (Events API browser) — khớp snippet Ads Manager:
 * ttq.load(PIXEL_ID); ttq.page();
 * PIXEL_ID lấy từ product.tiktokPixelCode (không hardcode).
 */
function ensureTtqStub() {
  if (typeof window === 'undefined') return;
  const t = 'ttq';
  window.TiktokAnalyticsObject = t;
  // Official: w[t] = w[t] || []
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let ttq: any = window.ttq;
  if (!Array.isArray(ttq) || !ttq.methods) {
    ttq = window.ttq = window.ttq || [];
    ttq.methods = [
      'page',
      'track',
      'identify',
      'instances',
      'debug',
      'on',
      'off',
      'once',
      'ready',
      'alias',
      'group',
      'enableCookie',
      'disableCookie',
      'holdConsent',
      'revokeConsent',
      'grantConsent',
    ];
    // Official: t.push([e].concat(Array.prototype.slice.call(arguments,0)))
    ttq.setAndDefer = function setAndDefer(target: any, method: string) {
      // eslint-disable-next-line prefer-rest-params, func-names
      target[method] = function () {
        target.push([method].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    for (let i = 0; i < ttq.methods.length; i += 1) {
      ttq.setAndDefer(ttq, ttq.methods[i]);
    }
    ttq.instance = function instance(pixelId: string) {
      // Official: ttq._i[t] || []
      const e = (ttq._i && ttq._i[pixelId]) || [];
      for (let n = 0; n < ttq.methods.length; n += 1) {
        ttq.setAndDefer(e, ttq.methods[n]);
      }
      return e;
    };
    ttq.load = function load(pixelId: string, opts?: Record<string, unknown>) {
      const r = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      ttq._i = ttq._i || {};
      ttq._i[pixelId] = [];
      ttq._i[pixelId]._u = r;
      ttq._t = ttq._t || {};
      ttq._t[pixelId] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[pixelId] = opts || {};
      // Dedupe: tránh inject events.js 2 lần khi H5 gọi ensure nhiều lần
      if (document.querySelector(`script[src*="analytics.tiktok.com/i18n/pixel/events.js"][src*="sdkid=${pixelId}"]`)) {
        return;
      }
      const n = document.createElement('script');
      n.type = 'text/javascript';
      n.async = true;
      n.src = `${r}?sdkid=${pixelId}&lib=${t}`;
      const e = document.getElementsByTagName('script')[0];
      e?.parentNode?.insertBefore(n, e);
    };
  }
}

function isTtPixelReady(pixelId: string): boolean {
  if (window.__joyH5TtPixelInited?.[pixelId]) return true;
  try {
    if (window.ttq?._i && window.ttq._i[pixelId]) return true;
  } catch {
    // ignore
  }
  return false;
}

/**
 * Inject Meta Pixel + GTM/GA4 + TikTok Pixel theo product.
 * Có GTM → không fbq('init') (tránh Duplicate nếu GTM đã gắn cùng Pixel ID).
 */
export function ensureTrackingScripts(config: H5TrackingConfig = {}) {
  if (typeof window === 'undefined') return;

  const facebookPixelId = normId(config.facebookPixelId);
  const googleId = normId(config.gtmContainerId);
  const tiktokPixelCode = normId(config.tiktokPixelCode);
  if (!facebookPixelId && !googleId && !tiktokPixelCode) {
    return;
  }

  window.__joyH5FbPixelInited = window.__joyH5FbPixelInited || {};
  window.__joyH5GoogleInited = window.__joyH5GoogleInited || {};
  window.__joyH5TtPixelInited = window.__joyH5TtPixelInited || {};

  const gtmMode = !!(googleId && isGtmId(googleId));

  // Meta Pixel: chỉ init trực tiếp khi KHÔNG dùng GTM (GTM thường đã có tag Meta Pixel).
  if (facebookPixelId) {
    if (gtmMode) {
      // (skip fbq init vì có GTM)
    } else if (!isFbPixelReady(facebookPixelId)) {
      ensureFbqStub();
      window.fbq?.('init', facebookPixelId);
      window.fbq?.('track', 'PageView');
      window.__joyH5FbPixelInited[facebookPixelId] = true;
      console.log(LOG, 'Meta Pixel: init + PageView', facebookPixelId);
    } else {
      window.__joyH5FbPixelInited[facebookPixelId] = true;
    }
  }

  // TikTok Pixel — luôn init client (không phụ thuộc GTM), giống Meta khi không GTM.
  if (tiktokPixelCode) {
    if (!isTtPixelReady(tiktokPixelCode)) {
      ensureTtqStub();
      window.ttq?.load(tiktokPixelCode);
      window.ttq?.page();
      window.__joyH5TtPixelInited[tiktokPixelCode] = true;
      console.log(LOG, 'TikTok Pixel: load + page', tiktokPixelCode);
    } else {
      window.__joyH5TtPixelInited[tiktokPixelCode] = true;
    }
  }

  if (!googleId) {
    return;
  }
  if (window.__joyH5GoogleInited[googleId]) {
    return;
  }

  if (isGa4Id(googleId)) {
    ensureGtag();
    if (!document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${googleId}"]`)) {
      const s = document.createElement('script');
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${googleId}`;
      document.head.appendChild(s);
    }
    window.gtag!('js', new Date());
    window.gtag!('config', googleId);
    window.__joyH5GoogleInited[googleId] = true;
    console.log(LOG, 'Google: GA4 gtag config', googleId);
    return;
  }

  if (!isGtmId(googleId)) {
    console.warn(LOG, `gtmContainerId="${googleId}" không hợp lệ. Dùng GTM-XXXX hoặc G-XXXX.`);
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });
  if (!document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${googleId}"]`)) {
    const j = document.createElement('script');
    j.async = true;
    j.src = `https://www.googletagmanager.com/gtm.js?id=${googleId}`;
    const f = document.getElementsByTagName('script')[0];
    f?.parentNode?.insertBefore(j, f);
    console.log(LOG, 'Google: inject gtm.js', j.src);
  } else {
    console.log(LOG, 'Google: gtm.js đã có trên trang', googleId);
  }
  window.__joyH5GoogleInited[googleId] = true;
  console.log(LOG, 'Google: GTM ready', {
    googleId,
    dataLayerLength: window.dataLayer.length,
  });
}

export function trackLoginSuccess({
  loginMethod,
  userId,
  productCode,
  tracking,
}: {
  loginMethod?: string;
  userId?: string | number;
  productCode?: string;
  tracking?: H5TrackingConfig;
}) {
  console.log(LOG, 'trackLoginSuccess start', { loginMethod, userId, productCode, tracking });
  ensureTrackingScripts(tracking || {});

  const method = loginMethod || 'unknown';
  const user_id = userId != null ? String(userId) : '';
  const product_code = productCode || '';
  const googleId = normId(tracking?.gtmContainerId);
  const facebookPixelId = normId(tracking?.facebookPixelId);
  const tiktokPixelCode = normId(tracking?.tiktokPixelCode);

  // GA4 recommended event: login — https://developers.google.com/analytics/devguides/collection/ga4/reference/events#login
  if (googleId && isGtmId(googleId)) {
    window.dataLayer = window.dataLayer || [];
    const payload = {
      event: 'login',
      method,
      login_method: method,
      user_id,
      product_code,
    };
    window.dataLayer.push(payload);
    console.log(LOG, 'Google GTM: dataLayer.push login', payload, {
      dataLayerLength: window.dataLayer.length,
      last: window.dataLayer[window.dataLayer.length - 1],
      gtmScript: !!document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${googleId}"]`),
    });
  } else if (googleId && isGa4Id(googleId)) {
    if (typeof window.gtag === 'function') {
      const payload = {
        method,
        login_method: method,
        user_id,
        product_code,
      };
      window.gtag('event', 'login', payload);
      console.log(LOG, 'Google GA4: gtag event login', googleId, payload);
    } else {
      console.warn(LOG, 'Google GA4: gtag chưa sẵn sàng', googleId);
    }
  } else {
    console.warn(LOG, 'Google: không push login — gtmContainerId thiếu hoặc sai', {
      gtmContainerId: tracking?.gtmContainerId,
      googleId,
    });
  }

  // Meta không có standard "Login". Tắt gửi LoginSuccess (không track login lên Facebook).
  // Optimize Ads đăng ký: CompleteRegistration trong trackRegisterSuccess.
  // if (facebookPixelId && window.fbq) {
  //   const metaPayload = {
  //     login_method: method,
  //     user_id,
  //     product_code,
  //   };
  //   window.fbq('trackCustom', 'LoginSuccess', metaPayload);
  //   console.log(LOG, 'Meta Pixel: trackCustom LoginSuccess', facebookPixelId, metaPayload);
  // } else if (facebookPixelId && !window.fbq) {
  //   console.warn(LOG, 'Meta Pixel: có ID nhưng fbq chưa có (đợi GTM load Pixel, hoặc bỏ GTM để H5 tự init)');
  // }
  void facebookPixelId;

  // TikTok Pixel — chỉ dùng param chuẩn TikTok Ads (content_id / content_name…).
  // user_id → ttq.identify({ external_id }) — không nhét vào properties event.
  // LoginSuccess = custom → Test Events OK nhưng KHÔNG optimize campaign (doc Custom Events).
  // Optimize Lead: CompleteRegistration (standard) trong trackRegisterSuccess.
  if (tiktokPixelCode && window.ttq) {
    const identifyPayload = user_id ? { external_id: user_id } : null;
    const ttPayload: Record<string, string> = {};
    if (product_code) ttPayload.content_id = product_code;
    if (method) ttPayload.content_name = `login_${method}`;
    if (identifyPayload) {
      window.ttq.identify(identifyPayload);
      console.log(LOG, 'TikTok Pixel: identify', tiktokPixelCode, identifyPayload);
    }
    window.ttq.track('LoginSuccess', ttPayload);
    console.log(LOG, 'TikTok Pixel: track LoginSuccess (custom)', tiktokPixelCode, ttPayload);
  } else if (tiktokPixelCode && !window.ttq) {
    console.warn(LOG, 'TikTok Pixel: có ID nhưng ttq chưa có');
  }
}

/** Đăng ký mới — GA4 `sign_up`, Meta/TikTok standard `CompleteRegistration`. */
export function trackRegisterSuccess({
  loginMethod,
  userId,
  productCode,
  tracking,
}: {
  loginMethod?: string;
  userId?: string | number;
  productCode?: string;
  tracking?: H5TrackingConfig;
}) {
  console.log(LOG, 'trackRegisterSuccess start', { loginMethod, userId, productCode, tracking });
  ensureTrackingScripts(tracking || {});

  const method = loginMethod || 'unknown';
  const user_id = userId != null ? String(userId) : '';
  const product_code = productCode || '';
  const googleId = normId(tracking?.gtmContainerId);
  const facebookPixelId = normId(tracking?.facebookPixelId);
  const tiktokPixelCode = normId(tracking?.tiktokPixelCode);

  // GA4 recommended: sign_up — https://developers.google.com/analytics/devguides/collection/ga4/reference/events#sign_up
  if (googleId && isGtmId(googleId)) {
    window.dataLayer = window.dataLayer || [];
    const payload = {
      event: 'sign_up',
      method,
      login_method: method,
      user_id,
      product_code,
    };
    window.dataLayer.push(payload);
    console.log(LOG, 'Google GTM: dataLayer.push sign_up', payload, {
      dataLayerLength: window.dataLayer.length,
      last: window.dataLayer[window.dataLayer.length - 1],
    });
  } else if (googleId && isGa4Id(googleId)) {
    if (typeof window.gtag === 'function') {
      const payload = {
        method,
        login_method: method,
        user_id,
        product_code,
      };
      window.gtag('event', 'sign_up', payload);
      console.log(LOG, 'Google GA4: gtag event sign_up', googleId, payload);
    } else {
      console.warn(LOG, 'Google GA4: gtag chưa sẵn sàng', googleId);
    }
  } else {
    console.warn(LOG, 'Google: không push sign_up — gtmContainerId thiếu hoặc sai', {
      gtmContainerId: tracking?.gtmContainerId,
      googleId,
    });
  }

  // Meta standard: CompleteRegistration
  if (facebookPixelId && window.fbq) {
    const metaPayload = {
      status: true,
      content_name: method,
      user_id,
      product_code,
    };
    window.fbq('track', 'CompleteRegistration', metaPayload);
    console.log(LOG, 'Meta Pixel: track CompleteRegistration', facebookPixelId, metaPayload);
  } else if (facebookPixelId && !window.fbq) {
    console.warn(LOG, 'Meta Pixel: có ID nhưng fbq chưa có (đợi GTM load Pixel, hoặc bỏ GTM để H5 tự init)');
  }

  // TikTok standard CompleteRegistration — Lead objective / optimize Ads.
  // https://ads.tiktok.com/help/article/standard-events-parameters
  // Chỉ gửi param chuẩn; identity qua identify(external_id).
  if (tiktokPixelCode && window.ttq) {
    const identifyPayload = user_id ? { external_id: user_id } : null;
    const ttPayload: Record<string, string> = {};
    if (product_code) ttPayload.content_id = product_code;
    if (method) ttPayload.content_name = `register_${method}`;
    if (identifyPayload) {
      window.ttq.identify(identifyPayload);
      console.log(LOG, 'TikTok Pixel: identify', tiktokPixelCode, identifyPayload);
    }
    window.ttq.track('CompleteRegistration', ttPayload);
    console.log(LOG, 'TikTok Pixel: track CompleteRegistration (standard)', tiktokPixelCode, ttPayload);
  } else if (tiktokPixelCode && !window.ttq) {
    console.warn(LOG, 'TikTok Pixel: có ID nhưng ttq chưa có');
  }
}
